import { Core } from "@faire/design-tokens";
import { ILayoutElement } from "@faire/web-api/indigofair/data/ILayoutElement";
import { Column, Flex, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React from "react";
import styled from "styled-components";

import { FilterInfoSticky } from "@faire/retailer/components/Filters/FilterInfoSticky";
import { FilterName } from "@faire/retailer/components/Filters/FilterName";
import { useFilterStore } from "@faire/retailer/components/Filters/useFilterStore";
import { NoResultsContainer } from "@faire/retailer/components/NoResults";
import { useSettingLoggedOutCategoryPageRedesign } from "@faire/retailer/settings/useSettingLoggedOutCategoryPageRedesign";
import { useCategoryPageScroll } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/CategoryPageShareables/hooks/useCategoryPageScroll";
import { LoggedOutCategoryPageBreadcrumb } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageAboveFold/LoggedOutCategoryPageBreadcrumb";
import { LoggedOutCategoryPageHelmet } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageAboveFold/LoggedOutCategoryPageHelmet";
import { LoggedOutCategoryPageFilterSidebar } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageFilterAndResults/LoggedOutCategoryPageFilterSidebar";
import { LoggedOutCategoryPageHeaderAndCarousel } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageFilterAndResults/LoggedOutCategoryPageHeaderAndCarousel";
import { LoggedOutCategoryPageProductList } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageFilterAndResults/LoggedOutCategoryPageProductList";
import { LoggedOutCategoryPageLayoutElements } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageLayoutElements/LoggedOutCategoryPageLayoutElements";
import { useLoggedOutCategoryPageisLoadedAndHasNoResults } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/hooks/useLoggedOutCategoryPageisLoadedAndHasNoResults";

export const LoggedOutCategoryPageAboveFold: React.FC<{
  categoryPageLoadTimingRef?: React.Ref<HTMLDivElement>;
}> = ({ categoryPageLoadTimingRef }) => {
  const { isTabletAndBelow } = useViewport();

  const isLoadedAndHasNoResults =
    useLoggedOutCategoryPageisLoadedAndHasNoResults();

  const { scrollRef } = useCategoryPageScroll();

  const {
    clearAllFilters,
    data: { activeFilterCount },
  } = useFilterStore(["activeFilterCount"]);
  const isLoggedOutCategoryPageRedesign =
    useSettingLoggedOutCategoryPageRedesign();

  if (isLoggedOutCategoryPageRedesign) {
    return (
      <>
        <LoggedOutCategoryPageHelmet />
        {/* hero banner on C1 */}
        <LoggedOutCategoryPageLayoutElements
          placement={[ILayoutElement.Placement.ABOVE_TITLE]}
        />

        <Wrapper style={{ maxWidth: "1424px" }} direction="column">
          {/* TODO (Alier): move breadcrumb to layout element after BE is done */}
          <StyledBreadcrumbWithLOCPRedesign />
          {/* TODO (Alier): remove this element after BE is done */}
          <LoggedOutCategoryPageLayoutElements
            placement={[ILayoutElement.Placement.BELOW_TITLE]}
          />
          <LoggedOutCategoryPageLayoutElements
            placement={[ILayoutElement.Placement.BELOW_BREADCRUMB]}
          />
          <Row>
            <StyleLoggedOutCategoryPageFilterSidebar />
            <Column style={{ width: "100%" }}>
              <div ref={scrollRef} />
              {isLoadedAndHasNoResults ? (
                <NoResultsContainer
                  showProductFirstResults
                  hasFiltersApplied={activeFilterCount > 0}
                  onClearFilters={clearAllFilters}
                  divRef={categoryPageLoadTimingRef}
                />
              ) : (
                <>
                  <FilterInfoSticky
                    name={FilterName.LOGGED_OUT_CATEGORY}
                    shouldNotShowFilterCollapsePillOnTabletAndBelow
                  />
                  <div
                    ref={categoryPageLoadTimingRef}
                    style={{ paddingTop: getSpacing("1x") }}
                  >
                    <LoggedOutCategoryPageProductList />
                  </div>
                </>
              )}
            </Column>
          </Row>
        </Wrapper>
      </>
    );
  }

  return (
    <>
      <LoggedOutCategoryPageHelmet />
      <StyledLoggedOutCategoryPageBreadcrumb />
      <Wrapper style={{ maxWidth: "1280px" }} direction="row">
        <LoggedOutCategoryPageFilterSidebar />
        <Column style={{ width: "100%" }}>
          <LoggedOutCategoryPageHeaderAndCarousel />
          <Spacer height="3x" />
          <div ref={scrollRef} />
          {isLoadedAndHasNoResults ? (
            <NoResultsContainer
              showProductFirstResults
              hasFiltersApplied={activeFilterCount > 0}
              onClearFilters={clearAllFilters}
              divRef={categoryPageLoadTimingRef}
            />
          ) : (
            <>
              {isTabletAndBelow ? (
                <FilterInfoSticky name={FilterName.LOGGED_OUT_CATEGORY} />
              ) : null}
              <div
                ref={categoryPageLoadTimingRef}
                style={{ paddingTop: getSpacing("1x") }}
              >
                <LoggedOutCategoryPageProductList />
              </div>
            </>
          )}
        </Column>
      </Wrapper>
    </>
  );
};

const Wrapper = styled(Flex)`
  height: 100%;
  width: 100%;
  margin: 0 auto;
  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing(0, "5x")};
  }

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing(0, "2x")};
  }
`;

const StyleLoggedOutCategoryPageFilterSidebar = styled(
  LoggedOutCategoryPageFilterSidebar
)`
  padding-top: ${getSpacing("2x")};
`;

const StyledBreadcrumbWithLOCPRedesign = styled(
  LoggedOutCategoryPageBreadcrumb
)`
  padding: ${getSpacing("4x", 0, "1x", 0)};
  ${responsiveMediaQueries.tabletAndBelow} {
    padding: ${getSpacing("3x", 0, "1x", 0)};
  }
`;

const StyledLoggedOutCategoryPageBreadcrumb = styled(
  LoggedOutCategoryPageBreadcrumb
)`
  padding: 14px 20px;
  margin-bottom: 0;

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: 14px 25px;
    margin-bottom: ${getSpacing("2x")};
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    padding: 14px 15px;
  }
  border-bottom: 1px solid ${Core.border.muted};
`;
